import {Box, Skeleton} from '@mantine/core';
import Head from 'next/head';
import Image from 'next/image';
import {useRouter} from 'next/router';
import type React from 'react';
import {MdArrowBack} from 'react-icons/md';
import PREPARE_WITH_AI from '../../../../../public/next-image/reportAnalytics/logo.png';
import {
  HOME,
  MYPAGE_REPORTS_ADD,
  REPORTS,
  REPORT_ANALYTICS_SEARCH,
} from '../../../../config/constants/routes';
import {Crumb} from '../../../../core/entities/reports/constants';
import {BreadcrumbsComponent} from '../../../../shared/components/BreadcrumbsComponent';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {FollowCompanyTypeC} from '../../../../shared/components/FollowCompany/TypeC';
import {useIsAppWebView} from '../../../../shared/hooks/useIsAppWebView';
import {buildCompanyFollowGtmContent} from '../../../../shared/services/buildCompanyFollowGtmContent';
import {createBreadCrumbsStructuredData} from '../../../../shared/services/createBreadCrumbsStructuredData';
import {createJsonLdHtml} from '../../../../shared/services/createJsonLdHtml';
import {getAIGradient} from '../../../reportAnalytics/usecase/getAIGradient';

type Props = {
  company?: {
    id: number;
    name: string;
    logoURL: string;
    industryName: string;
    industryCategoryName: string;
    industryCategoryID: number;
    followerCount: number;
    followed: boolean;
  };
  companyName?: string;
};

export const ExperiencesHeader: React.FC<Props> = ({company, companyName}) => {
  const isWebView = useIsAppWebView();
  const router = useRouter();

  if (!company) {
    return (
      <div className="w-full h-[250px] pc:h-[215px]">
        <Skeleton height={25} className="my-8" />
        <Skeleton className="h-[210px] pc:h-[175px]" />
      </div>
    );
  }

  const title = `${companyName}のインターン・本選考のES・選考体験記一覧`;
  const description = `${companyName}
  の本選考、インターンシップ選考で内定もしくは最終面接まで進んだ方からいただいた非常に信頼度が高い情報をまとめております。学生時代頑張ったことや志望動機などエントリーシートの書き方をはじめとした、Webテスト・適性検査の形式やGD（グループディスカッション）の問題、面接の質問と受け答え、フェルミ推定・ケース面接の心構えや解答などを掲載しております。さらに企業説明会やOB訪問、リクルーター面談で内定者が社員の方に伺った内容も多数記載しています。ぜひ、企業研究や自己分析にお役立てください。`;

  const crumbs = [
    {label: Crumb.HOME, href: HOME},
    {label: Crumb.REPORTS, href: REPORTS},
    {
      label: company.industryCategoryName,
      href: `${REPORTS}/industry/${company.industryCategoryID}`,
    },
    {
      label: title,
      href: `/company/${company.id}/experiences`,
    },
  ];

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={createJsonLdHtml(
            createBreadCrumbsStructuredData(crumbs),
          )}
        />
      </Head>
      <div className="hidden pc:block">
        <div className="py-8">
          <BreadcrumbsComponent crumbs={crumbs} />
        </div>

        <div className="shadow-card rounded-[20px] bg-white p-16">
          <h1 className="text-xl text-black font-bold leading-relaxed">
            {title}
          </h1>
          <div className="flex gap-16">
            <div>
              <CommonLink
                href={`/company/${company.id}`}
                className="items-center"
              >
                {company.logoURL && (
                  <Image
                    src={company.logoURL}
                    width={120}
                    height={80}
                    alt={`${companyName}のロゴ`}
                  />
                )}
              </CommonLink>
            </div>
            <div className="grow">
              <div className="text-black-2 text-base mt-8">
                {company.industryName}
              </div>
              <Box className="text-sm text-black-2 mt-8">{description}</Box>
            </div>
            <div className="w-[150px] flex flex-col gap-8">
              <FollowCompanyTypeC
                companyId={company.id}
                initialFollowerCount={company.followerCount}
                isInitiallyFollowed={company.followed}
                dataGtmContent={buildCompanyFollowGtmContent({
                  companyId: company.id,
                  companyName: companyName ?? '',
                  location: 'report-experiences',
                })}
              />
              <Box
                className={
                  'rounded-[32px] w-[150px] h-[41px] flex items-center justify-center cursor-pointer'
                }
                onClick={() => {
                  void router.push(REPORT_ANALYTICS_SEARCH);
                }}
                style={{
                  background: getAIGradient(),
                }}
              >
                <Image
                  src={PREPARE_WITH_AI}
                  className="w-[54px] h-[20px]"
                  alt="AI選考対策"
                />
              </Box>
              <CommonLink
                className="w-full p-8 rounded-lg items-center gap-2 text-primary text-sm font-bold leading-[14px] justify-center inline-flex"
                href={MYPAGE_REPORTS_ADD}
              >
                <span>ES・体験記を投稿</span>
              </CommonLink>
            </div>
          </div>
        </div>
      </div>
      <div className="pc:hidden bg-white rounded-[20px] p-8 mt-16 mx-16 shadow-card">
        {isWebView ? null : (
          <CommonLink href={REPORTS}>
            <MdArrowBack
              className="bg-background rounded-[20px] p-4 mx-8 my-4"
              size={24}
            />
          </CommonLink>
        )}
        <h1 className="text-xl text-black font-bold leading">{title}</h1>
      </div>
      <div className="pc:hidden p-16 mt-8 mx-16 shadow-card bg-white rounded-t-[20px]">
        <div className="flex gap-16">
          <CommonLink href={`/company/${company.id}`} className="items-center">
            {company.logoURL && (
              <Image
                alt={`${companyName}のロゴ`}
                src={company.logoURL}
                width={75}
                height={50}
              />
            )}
          </CommonLink>
          <div className="grow">
            <div className="text-black-2 text-sm">{company.industryName}</div>
            <Box className="text-sm text-black-2 mt-8 text-ellipsis line-clamp-2">
              {description}
            </Box>
          </div>
        </div>
        <div className="mt-16 flex justify-between">
          <FollowCompanyTypeC
            companyId={company.id}
            initialFollowerCount={company.followerCount}
            isInitiallyFollowed={company.followed}
            dataGtmContent={buildCompanyFollowGtmContent({
              companyId: company.id,
              companyName: companyName ?? '',
              location: 'report-experiences',
            })}
          />
          <Box
            className={
              'rounded-[32px] w-[94px] h-[44px] flex items-center justify-center cursor-pointer'
            }
            onClick={() => {
              if (isWebView) {
                location.href = REPORT_ANALYTICS_SEARCH;
              } else {
                void router.push(REPORT_ANALYTICS_SEARCH);
              }
            }}
            style={{
              background: getAIGradient(),
            }}
          >
            <Image
              src={PREPARE_WITH_AI}
              className="w-[54px] h-[20px]"
              alt="AI選考対策"
            />
          </Box>
        </div>
      </div>
    </>
  );
};
